import { AlarmClock, Bot, Check, KeyRound, PlugZap, RefreshCw, ShieldCheck, UserCog, UserPlus } from 'lucide-react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import GuideLayout from '../components/GuideLayout';
import GuideSection from '../components/GuideSection';
import imgLogin from './assets/wrl-01-login.png';
import imgUserMgmt from './assets/wrl-02-usermgmt.png';
import imgAddUser from './assets/wrl-03-adduser.png';
import imgForm from './assets/wrl-04-form.png';
import imgSuccess from './assets/wrl-05-success.png';
import imgViewProfile from './assets/wrl-06-viewprofile.png';
import imgToken from './assets/wrl-07-token.jpg';
import imgBopsCreds from './assets/wrl-08-bops-creds.png';
import {
  BodyText,
  BulletDot,
  BulletItemWrapper,
  BulletList,
  Callout,
  CalloutBody,
  CalloutTitle,
  CheckItemWrapper,
  CheckList,
  FigureCaption,
  FigureGrid,
  FigureImage,
  FormTable,
  FormTableGrid,
  FormTableLabel,
  FormTableValue,
  GuideBadge,
  GuideDivider,
  IndentedSection,
  InlineCode,
  NumBadge,
  PrereqBody,
  PrereqCard,
  PrereqIconWrap,
  PrereqsGrid,
  PrereqTitle,
  StepBody,
  StepItem,
  StepItemContent,
  StepList,
  StyledFigure,
  TimelineBadge,
  TimelineContent,
  TimelineLeft,
  TimelineLine,
  TimelineTitle,
  TimelineWrapper,
} from './WrlGuide.styles';

const Figure = ({ src, alt, caption }: { src: string; alt: string; caption: ReactNode }) => (
  <StyledFigure>
    <FigureImage src={src} alt={alt} loading="lazy" />
    <FigureCaption>{caption}</FigureCaption>
  </StyledFigure>
);

const PurpleBullet = ({ children }: { children: ReactNode }) => (
  <BulletItemWrapper>
    <BulletDot $color="#a2a5ff" />
    <span>{children}</span>
  </BulletItemWrapper>
);

const GrayBullet = ({ children }: { children: ReactNode }) => (
  <BulletItemWrapper>
    <BulletDot $color="#98A2B3" />
    <span>{children}</span>
  </BulletItemWrapper>
);

const CheckItem = ({ children }: { children: ReactNode }) => (
  <CheckItemWrapper>
    <Check size={16} style={{ color: '#12B76A', flexShrink: 0, marginTop: 2 }} />
    <span>{children}</span>
  </CheckItemWrapper>
);

const TimelineStep = ({
  n,
  title,
  children,
  last = false,
  dark = false,
}: {
  n: number;
  title: string;
  children: ReactNode;
  last?: boolean;
  dark?: boolean;
}) => (
  <TimelineWrapper>
    <TimelineLeft>
      <TimelineBadge $dark={dark}>{n}</TimelineBadge>
      {!last && <TimelineLine />}
    </TimelineLeft>
    <TimelineContent $last={last}>
      <TimelineTitle>{title}</TimelineTitle>
      {children}
    </TimelineContent>
  </TimelineWrapper>
);

const BOLD = <strong style={{ color: '#101828', fontWeight: 600 }} />;

const WrlGuide = () => {
  const { t } = useTranslation();

  const badge = (
    <GuideBadge>
      <PlugZap size={14} />
      {t('Guides.Wrl.badge')}
    </GuideBadge>
  );

  const subtitle = <Trans i18nKey="Guides.Wrl.subtitle" components={{ bold: BOLD }} />;

  const fieldKeys = ['name', 'email', 'phone', 'botAccount', 'employeeType', 'position', 'role', 'supplier'] as const;

  return (
    <GuideLayout title={t('Guides.Wrl.title')} subtitle={subtitle} badge={badge}>
      {/* REQUISITOS */}
      <GuideSection
        id="sec-requisitos"
        title={t('Guides.Wrl.Prereqs.title')}
        icon={<KeyRound size={17} />}
        description={t('Guides.Wrl.Prereqs.description')}>
        <PrereqsGrid>
          <PrereqCard>
            <PrereqIconWrap>
              <UserCog size={18} />
            </PrereqIconWrap>
            <PrereqTitle>{t('Guides.Wrl.Prereqs.Admin.title')}</PrereqTitle>
            <PrereqBody>{t('Guides.Wrl.Prereqs.Admin.description')}</PrereqBody>
          </PrereqCard>

          <PrereqCard>
            <PrereqIconWrap>
              <Bot size={18} />
            </PrereqIconWrap>
            <PrereqTitle>{t('Guides.Wrl.Prereqs.Bot.title')}</PrereqTitle>
            <PrereqBody style={{ marginBottom: 14 }}>
              {t('Guides.Wrl.Prereqs.Bot.descriptionPre')}
              <InlineCode>email+reports@company.com</InlineCode>
              {t('Guides.Wrl.Prereqs.Bot.descriptionPost')}
            </PrereqBody>
            <CheckList>
              <CheckItem>{t('Guides.Wrl.Prereqs.Bot.duty1')}</CheckItem>
              <CheckItem>{t('Guides.Wrl.Prereqs.Bot.duty2')}</CheckItem>
            </CheckList>
          </PrereqCard>
        </PrereqsGrid>
      </GuideSection>

      <GuideDivider />

      {/* CONFIGURAR */}
      <GuideSection
        id="sec-configurar"
        title={t('Guides.Wrl.Configure.title')}
        icon={<UserPlus size={17} />}
        description={t('Guides.Wrl.Configure.description')}>
        <TimelineStep n={1} title={t('Guides.Wrl.Configure.s1.title')}>
          <BodyText>{t('Guides.Wrl.Configure.s1.body')}</BodyText>
          <Figure
            src={imgLogin}
            alt={t('Guides.Wrl.Configure.s1.imgAlt')}
            caption={t('Guides.Wrl.Configure.s1.imgCaption')}
          />
        </TimelineStep>

        <TimelineStep n={2} title={t('Guides.Wrl.Configure.s2.title')}>
          <BulletList>
            <PurpleBullet>{t('Guides.Wrl.Configure.s2.b1')}</PurpleBullet>
            <PurpleBullet>
              <Trans i18nKey="Guides.Wrl.Configure.s2.b2" components={{ bold: BOLD }} />
            </PurpleBullet>
          </BulletList>
          <Figure
            src={imgUserMgmt}
            alt={t('Guides.Wrl.Configure.s2.imgAlt')}
            caption={t('Guides.Wrl.Configure.s2.imgCaption')}
          />
        </TimelineStep>

        <TimelineStep n={3} title={t('Guides.Wrl.Configure.s3.title')}>
          <BodyText $margin="0 0 6px">
            <Trans i18nKey="Guides.Wrl.Configure.s3.body" components={{ bold: BOLD }} />
          </BodyText>
          <Figure
            src={imgAddUser}
            alt={t('Guides.Wrl.Configure.s3.imgAddAlt')}
            caption={t('Guides.Wrl.Configure.s3.imgAddCaption')}
          />

          <BodyText $margin="22px 0 14px">{t('Guides.Wrl.Configure.s3.formIntro')}</BodyText>
          <FormTable>
            <FormTableGrid>
              {fieldKeys.map((key, i) => {
                const isLast = i === fieldKeys.length - 1;
                const label = t(`Guides.Wrl.Configure.s3.fields.${key}.label`);
                return [
                  <FormTableLabel key={`label-${key}`} $last={isLast}>
                    {label}
                  </FormTableLabel>,
                  <FormTableValue key={`value-${key}`} $last={isLast}>
                    <Trans i18nKey={`Guides.Wrl.Configure.s3.fields.${key}.value`} components={{ bold: BOLD }} />
                  </FormTableValue>,
                ];
              })}
            </FormTableGrid>
          </FormTable>

          <BodyText $margin="16px 0 0">
            <Trans i18nKey="Guides.Wrl.Configure.s3.submit" components={{ bold: BOLD }} />
          </BodyText>
          <FigureGrid>
            <Figure
              src={imgForm}
              alt={t('Guides.Wrl.Configure.s3.imgFormAlt')}
              caption={t('Guides.Wrl.Configure.s3.imgFormCaption')}
            />
            <Figure
              src={imgSuccess}
              alt={t('Guides.Wrl.Configure.s3.imgSuccessAlt')}
              caption={t('Guides.Wrl.Configure.s3.imgSuccessCaption')}
            />
          </FigureGrid>
        </TimelineStep>

        <TimelineStep n={4} title={t('Guides.Wrl.Configure.s4.title')}>
          <BulletList>
            <PurpleBullet>
              <Trans i18nKey="Guides.Wrl.Configure.s4.b1" components={{ bold: BOLD }} />
            </PurpleBullet>
            <PurpleBullet>{t('Guides.Wrl.Configure.s4.b2')}</PurpleBullet>
            <PurpleBullet>{t('Guides.Wrl.Configure.s4.b3')}</PurpleBullet>
            <PurpleBullet>
              <Trans i18nKey="Guides.Wrl.Configure.s4.b4" components={{ bold: BOLD }} />
            </PurpleBullet>
          </BulletList>
          <Figure
            src={imgViewProfile}
            alt={t('Guides.Wrl.Configure.s4.imgAlt')}
            caption={t('Guides.Wrl.Configure.s4.imgCaption')}
          />
        </TimelineStep>

        <TimelineStep n={5} title={t('Guides.Wrl.Configure.s5.title')} last>
          <BodyText>
            <Trans i18nKey="Guides.Wrl.Configure.s5.body" components={{ bold: BOLD }} />
          </BodyText>
          <Figure
            src={imgToken}
            alt={t('Guides.Wrl.Configure.s5.imgAlt')}
            caption={t('Guides.Wrl.Configure.s5.imgCaption')}
          />
        </TimelineStep>
      </GuideSection>

      <GuideDivider />

      {/* REGISTRAR */}
      <GuideSection
        id="sec-registrar"
        tocLabel={t('Guides.Wrl.Register.tocLabel')}
        title={t('Guides.Wrl.Register.title')}
        icon={<ShieldCheck size={17} />}
        description={t('Guides.Wrl.Register.description')}>
        <IndentedSection>
          <StepList>
            <StepItem>
              <NumBadge>1</NumBadge>
              <StepBody>{t('Guides.Wrl.Register.step1')}</StepBody>
            </StepItem>
            <StepItem>
              <NumBadge>2</NumBadge>
              <StepBody>
                <Trans i18nKey="Guides.Wrl.Register.step2" components={{ bold: BOLD }} />
              </StepBody>
            </StepItem>
            <StepItem>
              <NumBadge>3</NumBadge>
              <StepItemContent>
                <StepBody style={{ marginBottom: 10 }}>{t('Guides.Wrl.Register.step3Intro')}</StepBody>
                <CheckList>
                  <CheckItem>{t('Guides.Wrl.Register.check1')}</CheckItem>
                  <CheckItem>{t('Guides.Wrl.Register.check2')}</CheckItem>
                  <CheckItem>{t('Guides.Wrl.Register.check3')}</CheckItem>
                </CheckList>
              </StepItemContent>
            </StepItem>
          </StepList>

          <Callout>
            <AlarmClock size={20} style={{ color: '#B54708', flexShrink: 0, marginTop: 1 }} />
            <div>
              <CalloutTitle>{t('Guides.Wrl.Register.noteTitle')}</CalloutTitle>
              <CalloutBody>
                <Trans
                  i18nKey="Guides.Wrl.Register.note"
                  components={{ bold: <strong style={{ fontWeight: 700 }} /> }}
                />
              </CalloutBody>
            </div>
          </Callout>
        </IndentedSection>
      </GuideSection>

      <GuideDivider />

      {/* ACTUALIZAR */}
      <GuideSection
        id="sec-actualizar"
        tocLabel={t('Guides.Wrl.Update.tocLabel')}
        title={t('Guides.Wrl.Update.title')}
        icon={<RefreshCw size={17} />}
        description={<Trans i18nKey="Guides.Wrl.Update.description" components={{ bold: BOLD }} />}>
        <TimelineStep dark n={1} title={t('Guides.Wrl.Update.s1.title')}>
          <BodyText>{t('Guides.Wrl.Update.s1.body')}</BodyText>
        </TimelineStep>

        <TimelineStep dark n={2} title={t('Guides.Wrl.Update.s2.title')}>
          <BodyText>
            <Trans i18nKey="Guides.Wrl.Update.s2.body" components={{ bold: BOLD }} />
          </BodyText>
        </TimelineStep>

        <TimelineStep dark n={3} title={t('Guides.Wrl.Update.s3.title')}>
          <BodyText>{t('Guides.Wrl.Update.s3.body')}</BodyText>
        </TimelineStep>

        <TimelineStep dark n={4} title={t('Guides.Wrl.Update.s4.title')}>
          <BodyText>
            <Trans i18nKey="Guides.Wrl.Update.s4.body" components={{ bold: BOLD }} />
          </BodyText>
          <Figure
            src={imgToken}
            alt={t('Guides.Wrl.Update.s4.imgAlt')}
            caption={t('Guides.Wrl.Update.s4.imgCaption')}
          />
        </TimelineStep>

        <TimelineStep dark n={5} title={t('Guides.Wrl.Update.s5.title')}>
          <BulletList style={{ gap: 9 }}>
            <GrayBullet>
              <Trans i18nKey="Guides.Wrl.Update.s5.b1" components={{ bold: BOLD }} />
            </GrayBullet>
            <GrayBullet>{t('Guides.Wrl.Update.s5.b2')}</GrayBullet>
            <GrayBullet>{t('Guides.Wrl.Update.s5.b3')}</GrayBullet>
            <GrayBullet>{t('Guides.Wrl.Update.s5.b4')}</GrayBullet>
          </BulletList>
        </TimelineStep>

        <TimelineStep dark n={6} title={t('Guides.Wrl.Update.s6.title')} last>
          <BodyText>
            <Trans i18nKey="Guides.Wrl.Update.s6.body" components={{ bold: BOLD }} />
          </BodyText>
          <Figure
            src={imgBopsCreds}
            alt={t('Guides.Wrl.Update.s6.imgAlt')}
            caption={t('Guides.Wrl.Update.s6.imgCaption')}
          />
        </TimelineStep>
      </GuideSection>
    </GuideLayout>
  );
};

export default WrlGuide;
